/**
 * Caché de sesión de las tarjetas nuevas ya tokenizadas. Backend no tiene un endpoint para guardar
 * una tarjeta recién agregada: se guarda recién al completarse la operación (`initiate`), así que
 * mientras tanto el iframe la conserva acá (`sessionStorage`: dura lo que dura la sesión del
 * WebView; si no está disponible, memoria). Solo se guarda lo necesario para pagar (token, BIN,
 * últimos 4 y vencimiento), nunca el número completo ni el CVV.
 */
import type { SavedCard } from '../domain/models';

const STORAGE_KEY = 'carrefour-iframe-checkout.session-cards';
const MAX_CARDS = 10;

export interface SessionCardCache {
  /** Tarjetas vigentes: descarta las cuyo token de pago ya venció. */
  list(): SavedCard[];
  add(card: SavedCard): void;
  /** Quita una tarjeta (el token de pago es de un solo uso: se descarta tras cada intento). */
  remove(token: string): void;
  /** Vacía la caché (sesión invalidada): borra la clave de `sessionStorage` y la memoria. */
  clear(): void;
  /**
   * Asocia la caché a la sesión de checkout con la que se cargan las tarjetas. Si lo guardado
   * pertenece a otra sesión (o no tiene huella: formato anterior), se vacía antes de usarla. Con la
   * misma sesión no toca nada. El id es un token: solo se guarda una huella, nunca el id.
   */
  bindSession(checkoutSessionId: string): void;
}

/** Vigencia asumida del token de pago (backend: "menos de 10 minutos"). */
export const SESSION_CARD_TTL_MS = 8 * 60 * 1000;

const isSavedCard = (value: unknown): value is SavedCard =>
  typeof value === 'object' &&
  value !== null &&
  typeof (value as SavedCard).token === 'string' &&
  typeof (value as SavedCard).bin === 'string' &&
  typeof (value as SavedCard).lastFourDigits === 'string' &&
  typeof (value as SavedCard).paymentTokenCreatedAt === 'number';

const resolveStorage = (): Storage | null => {
  try {
    return window.sessionStorage;
  } catch {
    return null;
  }
};

/**
 * Huella corta (cyrb53, 53 bits, no criptográfica) del id de sesión: sirve para detectar que
 * cambió la sesión, no para autenticar, y no permite recuperar el id.
 */
const fingerprint = (value: string): string => {
  let h1 = 0xdeadbeef;
  let h2 = 0x41c6ce57;
  for (let i = 0; i < value.length; i += 1) {
    const code = value.charCodeAt(i);
    h1 = Math.imul(h1 ^ code, 2654435761);
    h2 = Math.imul(h2 ^ code, 1597334677);
  }
  h1 = Math.imul(h1 ^ (h1 >>> 16), 2246822507) ^ Math.imul(h2 ^ (h2 >>> 13), 3266489909);
  h2 = Math.imul(h2 ^ (h2 >>> 16), 2246822507) ^ Math.imul(h1 ^ (h1 >>> 13), 3266489909);
  return (4294967296 * (2097151 & h2) + (h1 >>> 0)).toString(36);
};

/** Lo guardado: las tarjetas y la huella de la sesión de checkout con la que se cargaron (si se conoce). */
interface Stored {
  sessionFingerprint?: string;
  cards: SavedCard[];
}

/** `storage` = `null` fuerza solo memoria (sin `sessionStorage` disponible). */
export const createSessionCardCache = (
  storage: Storage | null = resolveStorage(),
  now: () => number = Date.now,
): SessionCardCache => {
  let memory: Stored = { cards: [] };

  const read = (): Stored => {
    if (!storage) {
      return memory;
    }
    try {
      const parsed: unknown = JSON.parse(storage.getItem(STORAGE_KEY) ?? '[]');
      // Formato anterior: arreglo de tarjetas, sin huella de sesión.
      if (Array.isArray(parsed)) {
        return { cards: parsed.filter(isSavedCard) };
      }
      const record = parsed as { sessionFingerprint?: unknown; cards?: unknown } | null;
      if (record && Array.isArray(record.cards)) {
        return {
          ...(typeof record.sessionFingerprint === 'string' ? { sessionFingerprint: record.sessionFingerprint } : {}),
          cards: record.cards.filter(isSavedCard),
        };
      }
      return { cards: [] };
    } catch {
      // Storage ilegible o bloqueado: se usa lo que haya en memoria.
      return memory;
    }
  };

  const write = (stored: Stored): void => {
    memory = stored;
    try {
      storage?.setItem(STORAGE_KEY, JSON.stringify(stored));
    } catch {
      // Sin espacio o sin permisos: queda solo en memoria.
    }
  };

  const isFresh = (card: SavedCard): boolean =>
    now() - (card.paymentTokenCreatedAt ?? 0) < SESSION_CARD_TTL_MS;

  return {
    list: () => read().cards.filter(isFresh),
    add: (card) => {
      const { sessionFingerprint, cards } = read();
      const others = cards.filter((existing) => existing.token !== card.token && isFresh(existing));
      write({ ...(sessionFingerprint ? { sessionFingerprint } : {}), cards: [...others, card].slice(-MAX_CARDS) });
    },
    remove: (token) => {
      const { sessionFingerprint, cards } = read();
      write({
        ...(sessionFingerprint ? { sessionFingerprint } : {}),
        cards: cards.filter((existing) => existing.token !== token),
      });
    },
    clear: () => {
      memory = { cards: [] };
      try {
        storage?.removeItem(STORAGE_KEY);
      } catch {
        // Storage bloqueado: ya quedó vacía la memoria.
      }
    },
    bindSession: (checkoutSessionId) => {
      const current = fingerprint(checkoutSessionId);
      if (read().sessionFingerprint === current) {
        return;
      }
      write({ sessionFingerprint: current, cards: [] });
    },
  };
};
